import { ErrorMessage, Field, useField } from 'formik';
import React from 'react';

export interface FormFieldProps {
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  hint?: string;
  className?: string;
  id?: string;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  placeholder,
  required,
  disabled,
  leftIcon,
  rightIcon,
  hint,
  className = '',
  id,
  step,
  min,
  max,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;
  const fieldId = id || `field-${name}`;

  return (
    <div className={className}>
      {/* Label */}
      <label
        htmlFor={fieldId}
        className={`block text-sm font-normal mb-2 ${
          hasError ? 'text-error-500' : 'text-monday-text-muted'
        }`}
      >
        {label}
        {required && <span className="form-required-dot"></span>}
      </label>

      {/* Input Container */}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-monday-text-muted pointer-events-none z-10">
            {leftIcon}
          </div>
        )}

        <Field
          type={type}
          name={name}
          id={fieldId}
          placeholder={placeholder}
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          className={`
            mobile-form-input
            w-full
            ${leftIcon ? 'pl-10' : ''}
            ${rightIcon ? 'pr-10' : ''}
            ${hasError ? 'border-red-500' : ''}
          `}
        />

        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-monday-text-muted pointer-events-none z-10">
            {rightIcon}
          </div>
        )}
      </div>

      {/* Error Message */}
      <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />

      {/* Hint */}
      {hint && !hasError && (
        <p className="mt-1 text-xs text-monday-text-muted">{hint}</p>
      )}
    </div>
  );
};

export default FormField;
